import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { ITableKey } from 'src/app/modules/shared/components/basic-table/basic-table.constants';
import { IPaginationEvent, ITableButtonEvent, TABLE_CONSTANTS } from 'src/app/modules/shared/components/be-paginated-table/be-paginated-table.constants';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { IDashboardOperation, IDashboardOperationRequest } from 'src/app/modules/shared/models/operation.model';
import { IOperationFilter } from 'src/app/modules/shared/models/policy.model';
import { EUserVisibility } from 'src/app/modules/shared/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { FormUtils } from 'src/app/utils/FormUtils';
import { dashboard_form_keys, dashboard_form_template, dashboard_table_keys, EDashboardStatus, ongoing_operations } from './dashboard.constants';
import { DashboardDatasource } from './dashboard.datasource';
import { DashboardService } from './dashboard.service';
import moment = require('moment');

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {

  operations: IDashboardOperation[] = [];
  dataSource: DashboardDatasource;
  tableKeys: ITableKey[] = CommonUtils.assign(dashboard_table_keys);
  form: FormGroup;
  formKeys: IFormKey[] = CommonUtils.assign(dashboard_form_keys);
  notYetSearched = true;
  destroy$ = new Subject<void>();

  constructor(
    private readonly _router: Router,
    private readonly _dashboardService: DashboardService,
    private readonly _spinner: SpinnerService,
    private readonly _alertService: AlertService,
    private readonly _authService: AuthenticationService,
    private readonly _location: Location
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.manageForm();
    this.observeDataSource();
    this.retrieveOperations();
    this.checkNavigationState();
  }

  createForm() {
    this.form = FormUtils.initFormGroupFromTemplate(dashboard_form_template);
    LoggerService.logInfo(this.constructor.name, 'createForm', { form: this.form });
  }

  reset() {
    LoggerService.logInfo(this.constructor.name, 'reset');
    this.form.reset();
    this.form.get('dateFrom').disable();
    this.form.get('dateTo').disable();
    this.dataSource.resultSubject.next([]);
    this.dataSource.length = 0;
    this.notYetSearched = true;
    this.checkUserAbilitation();
  }

  checkUserAbilitation() {
    LoggerService.logInfo(this.constructor.name, 'checkUserAbilitation', { dashboardMode: this._authService.dashboardMode });
    if (this._authService.dashboardMode === EUserVisibility.F) {
      this.form.get('agencyCode').setValue(this._authService.loggedUser.idcedcorrente);
      this.form.get('agencyCode').disable();
    }
  }

  manageForm(): void {
    this.checkUserAbilitation();
    this.form.get('dateFrom').disable();
    this.form.get('dateTo').disable();
    this.form.get('timeframe').valueChanges.subscribe(() => {
      this.checkDates();
    });
  }

  retrieveOperations(): void {
    this._spinner.show();
    this._dashboardService.retrieveOpFilter()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: IOperationFilter[]) => {
          LoggerService.logInfo(this.constructor.name, 'retrieveOperations', { response });
          if (response && this.formKeys) {
            this.formKeys.forEach(key => {
              if (key?.controlName === 'operationId') {
                key.options = response.map(elem => ({ key: elem?.operationCode, value: elem?.operationDesc }));
              }
            })
          }
          this._spinner.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'retrieveOperations', { error });
          this._alertService.openErrorMessage('Errore nel recupero delle operazioni disponibili', 'retrieveOperations', error);
          this._spinner.hide();
        }
      })
  }

  checkDates(): void {
    const timeframeValue = this.form.get('timeframe').value;
    if (!this.form.get('dateFrom') || !this.form.get('dateTo')) {
      return;
    }
    switch (timeframeValue) {
      case 'TODAY':
        this.form.get('dateFrom').setValue(moment());
        this.form.get('dateFrom').disable();
        this.form.get('dateTo').setValue(moment());
        this.form.get('dateTo').disable();
        break;
      case 'LAST_WEEK':
        this.form.get('dateFrom').setValue(moment().subtract(7, 'days'));
        this.form.get('dateFrom').disable();
        this.form.get('dateTo').setValue(moment());
        this.form.get('dateTo').disable();
        break;
      case 'LAST_MONTH':
        this.form.get('dateFrom').setValue(moment().subtract(1, 'months'));
        this.form.get('dateFrom').disable();
        this.form.get('dateTo').setValue(moment());
        this.form.get('dateTo').disable();
        break;
      default:
        this.form.get('dateFrom').reset();
        this.form.get('dateFrom').enable();
        this.form.get('dateTo').reset();
        this.form.get('dateTo').enable();
    }
  }

  observeDataSource() {
    this.dataSource = new DashboardDatasource(this._dashboardService, this._alertService);
    this.dataSource.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(resp => {
        if (resp) {
          this._spinner.show();
        } else {
          this._spinner.hide();
        }
      });
  }

  getOperationsBody(pagination?: IPaginationEvent): IDashboardOperationRequest {
    const rawForm = this.form.getRawValue();
    const obj: IDashboardOperationRequest = {
      companyCode: (rawForm.agencyCode) || null,
      operationCode: (rawForm.operationId) || null,
      operationStatus: rawForm.status?.length > 0 ? (rawForm.status) : null,
      userId: (rawForm.userId) || null,
      companyCodes: (rawForm.companies) || null,
      startDate: rawForm.dateFrom ? (rawForm.dateFrom).startOf('day') : null,
      endDate: rawForm.dateTo ? (rawForm.dateTo).endOf('day') : null,
      pageIndex: (pagination?.pageIndex) || TABLE_CONSTANTS.START_PAGE,
      pageSize: (pagination?.pageSize) || TABLE_CONSTANTS.PAGE_SIZE,
      sortDirection: (pagination?.sortDirection) || TABLE_CONSTANTS.START_SORT,
      sortField: (pagination?.sortField) || null
    }
    LoggerService.logInfo(this.constructor.name, 'getOperationsBody', { obj });
    return obj;
  }

  checkNavigationState() {
    const state = this._location.getState();
    LoggerService.logInfo(this.constructor.name, 'getNavigationState', { state });
    if (state && state['filterByUser']) {
      this.form.get('userId').setValue(this._authService.loggedUser.matricola);
      this.form.get('status').setValue([EDashboardStatus.DRAFT, EDashboardStatus.TO_UPLOAD]);
      this.onSearchButtonClick();
    }
  }

  onSearchButtonClick() {
    LoggerService.logInfo(this.constructor.name, 'onSearchButtonClick');
    const body = this.getOperationsBody();
    this.getOperations(body);
  }

  onTableChange(event: IPaginationEvent) {
    LoggerService.logInfo(this.constructor.name, 'onTableChange', { event });
    const body = this.getOperationsBody(event);
    this.getOperations(body);
  }

  get searchButtonDisabled(): boolean {
    const atLeastOneFieldFilled = Object.values(this.form.value).some(value => value && value !== '');
    return !atLeastOneFieldFilled;
  }

  onDetailRequested(obj: ITableButtonEvent<IDashboardOperation>) {
    LoggerService.logInfo(this.constructor.name, 'onDetailRequested', { obj });
    // se operazione in corso si atterra direttamente su pagina ops, altrimenti su dettaglio polizza
    const operationStatus = CommonUtils.checkAndGet(obj, 'detailObject.status', null);
    if (ongoing_operations.includes(operationStatus)) {
      const companyId = CommonUtils.getCompanyCode(obj?.detailObject?.companyId);
      if (companyId) {
        this._router.navigate([`/policies/${obj?.detailObject?.bperContractNumber}/${companyId}/operations/${obj?.detailObject?.operationId}`], {
          state: {
            element: obj.detailObject,
            simulateOperation: false
          }
        });
      } else {
        this._alertService.openErrorMessage('Codice compagnia non riconosciuto', '');
      }
    } else {
      this._router.navigate([`/policies/${obj?.detailObject?.bperContractNumber}`]);
    }
  }

  getOperations(body: IDashboardOperationRequest) {
    LoggerService.logInfo(this.constructor.name, 'getOperations', { body });
    this.notYetSearched = false;
    this.dataSource.search(body);
  }

  ngOnDestroy(): void {
    this.dataSource.resultSubject.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

}
